import CloseIcon from '@mui/icons-material/Close';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import LoadingButton from '@mui/lab/LoadingButton';
import {
    Alert,
    Autocomplete,
    Box,
    CircularProgress,
    debounce,
    Divider,
    FormControlLabel,
    Grid,
    IconButton,
    List,
    ListItem,
    ListItemText,
    Paper,
    Skeleton,
    Stack,
    Switch,
    TextField,
    Theme,
    // Goals feature (commented out — base auto-kick only for now; restore from commits ce54e3b4 / 26d5d814):
    // ToggleButton,
    // ToggleButtonGroup,
    Typography,
    useTheme,
} from '@mui/material';
import { useDialog } from 'muibox';
import { useSnackbar } from 'notistack';
import { Fragment, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import BackNavigationButton from '../../../components/common/BackNavigationButton';
import LargeMaterialUISwitch from '../../../components/common/LargeMaterialUISwitch';
// Goals feature (commented out — base auto-kick only for now; restore from commits ce54e3b4 / 26d5d814):
// import LiveStreamGoalsEditor from '../../../components/services/liveStream/LiveStreamGoalsEditor';
import OnlyFansListsSelector from '../../../components/common/OnlyFansListsSelector';
import PageContainer from '../../../components/common/PageContainter';
import SliderSetting from '../../../components/common/SliderSetting';
import StyledCard from '../../../components/common/StyledCard';
import StyledCardTitleBar from '../../../components/common/StyledCardTitleBar';
import useSextLiveStreamForceAutoKick, {
    kickTimeWastersBlackList,
    kickTimeWastersWhiteList,
} from '../../../hooks/useSextForceLiveStreamAutoKick';
import useSubscriber from '../../../hooks/useSubscriber';
import useSubscriberUsernameSearchT from '../../../hooks/useSubscriberUsernameSearchT';
import useSubscriberUsernameSearchX from '../../../hooks/useSubscriberUsernameSearchX';
import { SettingsContext } from '../../../store/SettingsContext';
import { isSextforceActive } from '../../../utils/common';

type whiteListOption = {
    id: string;
    label: string;
};

const SextforceLiveStreamAutoKickFreeLoaders = () => {
    const settingsContext = useContext(SettingsContext);
    const theme: Theme = useTheme();
    const dialog = useDialog();
    const { enqueueSnackbar } = useSnackbar();
    const { data: subscriber, isLoading: isSubscriberLoading } = useSubscriber();
    const {
        data: theAutoKickSettings,
        refetch: refetchAutoKickSettings,
        updateAutoKickSettings,
        updateAutoKickSettingsService,
        selectAutoKickServiceStatus,
        kickAllCurrentNonSpenders,
        getAutoKickWhiteList,
        autoKickAddToWhiteList,
        autoKickRemoveFromWhiteList,
        getAutoKickBlackList,
        autoKickAddToBlackList,
        autoKickRemoveFromBlackList,
        // Goals feature (commented out — base auto-kick only for now; restore from commits ce54e3b4 / 26d5d814):
        // getKickMode,
        // setKickMode,
    } = useSextLiveStreamForceAutoKick();

    const [updateSettingsLoading, setUpdateSettingsLoading] = useState(false);

    const [serviceActive, setServiceActive] = useState<boolean>(false);
    // Goals feature (commented out — base auto-kick only for now; restore from commits ce54e3b4 / 26d5d814):
    // Auto Kick settings mode: false = whole-stream (one settings set), true = per-goal.
    // const [perGoalMode, setPerGoalMode] = useState<boolean>(false);
    // const [modeLoading, setModeLoading] = useState<boolean>(false);
    const [tippedHoursAgo, setTippedHoursAgo] = useState<number>(theAutoKickSettings?.tippedHoursAgo ?? 0);
    const [usersCanWatchMinutes, setUsersCanWatchMinutes] = useState<number>(theAutoKickSettings?.usersCanWatchMinutes ?? 10);
    const [usersCanWatchMinutesOnRejoin, setUsersCanWatchMinutesOnRejoin] = useState<number>(
        theAutoKickSettings?.usersCanWatchMinutesOnRejoin ?? 10,
    );
    const [numberOfRejoinsAllowed, setNumberOfRejoinsAllowed] = useState<number>(theAutoKickSettings?.numberOfRejoinsAllowed ?? 1);
    const [publicShame, setPublicShame] = useState<boolean>(theAutoKickSettings?.publicShameFan ?? false);
    const [sendWarningMessages, setSendWarningMessages] = useState<boolean>(theAutoKickSettings?.sendWarningMessages ?? true);
    const [onlySendFinalWarning, setOnlySendFinalWarning] = useState<boolean>(theAutoKickSettings?.onlySendFinalWarning ?? false);
    const [whiteListListId, setWhiteListListId] = useState<string>(theAutoKickSettings?.whiteListListId ?? '0');
    const [minimumTipAmount, setMinimumTipAmount] = useState<number>(theAutoKickSettings?.minimumTipAmount ?? 1);

    const [kickAllLoading, setKickAllLoading] = useState<boolean>(false);
    const [whiteList, setWhiteList] = useState<kickTimeWastersWhiteList[]>([]);
    const whitelistIds = useMemo(() => new Set(whiteList.map(w => w.fanId)), [whiteList]);
    const [queryStr, setQueryStr] = useState<string>('');
    const [queryStrDebounce, setQueryStrDebounce] = useState<string>('');
    const { data: searchResults, isLoading: whiteListLoading } = useSubscriberUsernameSearchT(queryStr);
    const [addWhiteListLoading, setAddWhiteListLoading] = useState<boolean>(false);
    const [removingFanId, setRemovingFanId] = useState<number | null>(null);

    const [blackList, setBlackList] = useState<kickTimeWastersBlackList[]>([]);
    const blacklistIds = useMemo(() => new Set(blackList.map(w => w.fanId)), [blackList]);
    const [blackQueryStr, setBlackQueryStr] = useState<string>('');
    const [blackQueryStrDebounce, setBlackQueryStrDebounce] = useState<string>('');
    const { data: blackSearchResults, isLoading: blackListLoading } = useSubscriberUsernameSearchX(blackQueryStr);
    const [addBlackListLoading, setAddBlackListLoading] = useState<boolean>(false);
    const [removingBlackFanId, setRemovingBlackFanId] = useState<number | null>(null);

    const setQueryStrValue = (value: string) => {
        setQueryStr(value);
    };

    const searchQueryDebounce = useCallback(debounce(setQueryStrValue, 750), []);

    const searchOnChange = (value: string) => {
        searchQueryDebounce(value);
        setQueryStrDebounce(value);
    };

    const setBlackQueryStrValue = (value: string) => {
        setBlackQueryStr(value);
    };

    const blackSearchQueryDebounce = useCallback(debounce(setBlackQueryStrValue, 750), []);

    const blackSearchOnChange = (value: string) => {
        blackSearchQueryDebounce(value);
        setBlackQueryStrDebounce(value);
    };

    // Track whether any settings have been changed from saved values
    const hasChanges = useMemo(() => {
        if (!theAutoKickSettings) return false;
        return (
            tippedHoursAgo !== theAutoKickSettings.tippedHoursAgo ||
            usersCanWatchMinutes !== theAutoKickSettings.usersCanWatchMinutes ||
            usersCanWatchMinutesOnRejoin !== theAutoKickSettings.usersCanWatchMinutesOnRejoin ||
            numberOfRejoinsAllowed !== theAutoKickSettings.numberOfRejoinsAllowed ||
            publicShame !== theAutoKickSettings.publicShameFan ||
            sendWarningMessages !== theAutoKickSettings.sendWarningMessages ||
            onlySendFinalWarning !== theAutoKickSettings.onlySendFinalWarning ||
            whiteListListId !== theAutoKickSettings.whiteListListId ||
            minimumTipAmount !== theAutoKickSettings.minimumTipAmount
        );
    }, [
        theAutoKickSettings,
        tippedHoursAgo,
        usersCanWatchMinutes,
        usersCanWatchMinutesOnRejoin,
        numberOfRejoinsAllowed,
        publicShame,
        sendWarningMessages,
        onlySendFinalWarning,
        whiteListListId,
        minimumTipAmount,
    ]);

    // Check if Sextforce is active for this subscriber
    const sextforceActive = useMemo(() => {
        if (subscriber && isSextforceActive(subscriber)) {
            return true;
        }
        return false;
    }, [subscriber]);

    useEffect(() => {
        const getList = async () => {
            const results = await getAutoKickWhiteList();
            setWhiteList(results);
        };
        getList();
    }, []);

    useEffect(() => {
        const getList = async () => {
            const results = await getAutoKickBlackList();
            setBlackList(results);
        };
        getList();
    }, []);

    useEffect(() => {
        if (theAutoKickSettings) {
            setTippedHoursAgo(theAutoKickSettings.tippedHoursAgo);
            setUsersCanWatchMinutes(theAutoKickSettings.usersCanWatchMinutes);
            setUsersCanWatchMinutesOnRejoin(theAutoKickSettings.usersCanWatchMinutesOnRejoin);
            setNumberOfRejoinsAllowed(theAutoKickSettings.numberOfRejoinsAllowed);
            setPublicShame(theAutoKickSettings.publicShameFan);
            setSendWarningMessages(theAutoKickSettings.sendWarningMessages);
            setOnlySendFinalWarning(theAutoKickSettings.onlySendFinalWarning);
            setWhiteListListId(theAutoKickSettings.whiteListListId);
            setMinimumTipAmount(theAutoKickSettings.minimumTipAmount);
        }
    }, [theAutoKickSettings]);

    useEffect(() => {
        if (!sextforceActive) {
            setServiceActive(false);
        } else {
            selectAutoKickServiceStatus()
                .then(active => setServiceActive(active))
                .catch(() => setServiceActive(false));
        }
    }, [sextforceActive]);

    /* Goals feature (commented out — base auto-kick only for now; restore from commits ce54e3b4 / 26d5d814):
    // Load the Auto Kick settings mode (whole-stream vs per-goal).
    useEffect(() => {
        if (!sextforceActive) {
            setPerGoalMode(false);
            return;
        }
        getKickMode()
            .then(mode => setPerGoalMode(mode))
            .catch(() => setPerGoalMode(false));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sextforceActive]);

    const changeKickMode = async (perGoal: boolean) => {
        if (perGoal === perGoalMode) return;
        setModeLoading(true);
        try {
            const ok = await setKickMode(perGoal);
            if (ok) {
                setPerGoalMode(perGoal);
            } else {
                enqueueSnackbar('Could not change the Auto Kick mode.', { variant: 'error', autoHideDuration: 3000 });
            }
        } finally {
            setModeLoading(false);
        }
    };
    */

    const updateAutoKick = async () => {
        if (theAutoKickSettings) {
            setUpdateSettingsLoading(true);
            const payload = {
                onlyfansId: theAutoKickSettings.onlyfansId,
                tippedHoursAgo,
                usersCanWatchMinutes,
                usersCanWatchMinutesOnRejoin,
                numberOfRejoinsAllowed,
                publicShameFan: publicShame,
                sendWarningMessages,
                onlySendFinalWarning,
                whiteListListId,
                minimumTipAmount,
            };
            const returnValue = await updateAutoKickSettings(payload);
            if (returnValue) {
                await refetchAutoKickSettings();
                enqueueSnackbar('Settings Saved Successfully!', { variant: 'success', autoHideDuration: 3000 });
            } else {
                enqueueSnackbar('Settings did not save successfully!', { variant: 'error', autoHideDuration: 3000 });
            }
            setUpdateSettingsLoading(false);
        }
    };

    const kickAllButton = async () => {
        setKickAllLoading(true);
        const result = await kickAllCurrentNonSpenders();
        setKickAllLoading(false);
        if (result) {
            enqueueSnackbar('All Non-Tippers Kicked Successfully!', { variant: 'success', autoHideDuration: 3000 });
        } else {
            enqueueSnackbar('Error trying to kick all non-spenders.', { variant: 'error', autoHideDuration: 3000 });
        }
    };

    const addToWhiteList = async (item: kickTimeWastersWhiteList | null) => {
        if (!item) return;

        setAddWhiteListLoading(true);

        try {
            searchOnChange('');

            const results = await autoKickAddToWhiteList(item);

            if (results) {
                enqueueSnackbar('Whitelist Updated Successfully!', { variant: 'success', autoHideDuration: 2000 });

                const list = await getAutoKickWhiteList();

                setWhiteList(list);
            } else {
                enqueueSnackbar('Error trying to add fan to whitelist.', { variant: 'error', autoHideDuration: 3000 });
            }
        } finally {
            setAddWhiteListLoading(false);
        }
    };

    const removeFromWhiteList = async (item: kickTimeWastersWhiteList) => {
        setRemovingFanId(item.fanId);

        try {
            const results = await autoKickRemoveFromWhiteList(item);

            if (results) {
                enqueueSnackbar('Whitelist Updated Successfully!', { variant: 'success', autoHideDuration: 2000 });

                const list = await getAutoKickWhiteList();

                setWhiteList(list);
            } else {
                enqueueSnackbar('Error trying to remove fan from whitelist.', { variant: 'error', autoHideDuration: 3000 });
            }
        } finally {
            setRemovingFanId(null);
        }
    };

    const addToBlackList = async (item: kickTimeWastersBlackList | null) => {
        if (!item) return;

        setAddBlackListLoading(true);

        try {
            blackSearchOnChange('');

            const results = await autoKickAddToBlackList(item);

            if (results) {
                enqueueSnackbar('Blacklist Updated Successfully!', { variant: 'success', autoHideDuration: 2000 });

                const list = await getAutoKickBlackList();

                setBlackList(list);
            } else {
                enqueueSnackbar('Error trying to add fan to blacklist.', { variant: 'error', autoHideDuration: 3000 });
            }
        } finally {
            setAddBlackListLoading(false);
        }
    };

    const removeFromBlackList = async (item: kickTimeWastersBlackList) => {
        setRemovingBlackFanId(item.fanId);

        try {
            const results = await autoKickRemoveFromBlackList(item);

            if (results) {
                enqueueSnackbar('Blacklist Updated Successfully!', { variant: 'success', autoHideDuration: 2000 });

                const list = await getAutoKickBlackList();

                setBlackList(list);
            } else {
                enqueueSnackbar('Error trying to remove fan from blacklist.', { variant: 'error', autoHideDuration: 3000 });
            }
        } finally {
            setRemovingBlackFanId(null);
        }
    };

    return (
        <PageContainer>
            {subscriber && '_id' in subscriber && subscriber._id && (
                <>
                    <Typography variant="h5" sx={{ mb: 2 }}>
                        <BackNavigationButton url={`/subscribers/${subscriber._id}/${settingsContext.services.sextforce.homeUrl}`} />
                        Auto Kick Freeloaders for {subscriber.username}
                    </Typography>

                    <StyledCard noCard noBottomMargin>
                        <StyledCardTitleBar title="Auto Kick Freeloaders" />
                    </StyledCard>
                    <StyledCard>
                        <Typography gutterBottom>
                            Tired of freeloaders watching your stream without spending a dime? OnlyStruggles Auto Kick Freeloaders monitors
                            your viewers and boots anyone who hasn't tipped — automatically, while you focus on performing.
                        </Typography>
                        <Typography gutterBottom>
                            Non-spenders get up to 3 warnings via private stream message before they're shown the door. Not a fan of the
                            clutter? Turn warnings off and let your{' '}
                            <Link
                                to={`/subscribers/${subscriber._id}/${settingsContext.services.sextforce.homeUrl}/liveStream/autoAnnouncements`}
                                style={{ textDecoration: 'none', fontWeight: 'bold' }}
                            >
                                Auto Announcement Rules
                            </Link>{' '}
                            do the talking.
                        </Typography>
                        <Typography gutterBottom>
                            <strong>Pro tip:</strong> Turn Auto Kick on <em>before</em> you go live for the smoothest experience. Enabling
                            it mid-stream means existing viewers finish their warning cycle first — but anyone who leaves and tries to sneak
                            back in gets booted on the spot.
                        </Typography>
                        <Typography gutterBottom>
                            Some fans just need a little nudge. Set how many times they can rejoin after being kicked and how long they get
                            to watch before they have to put their money where their eyes are.
                        </Typography>
                    </StyledCard>
                    <StyledCard>
                        <Grid container spacing={1} flexGrow={0} alignItems="center" justifyContent="center">
                            <Grid item xs>
                                Auto Kick is currently{' '}
                                {serviceActive ? (
                                    <span style={{ color: theme.palette.success.dark }}>running</span>
                                ) : (
                                    <span style={{ color: theme.palette.error.main }}>not running</span>
                                )}
                                .
                            </Grid>
                            <Grid item xs="auto">
                                {isSubscriberLoading ? (
                                    <Skeleton width={'115px'} height={'51px'} />
                                ) : (
                                    <LargeMaterialUISwitch
                                        checked={serviceActive}
                                        disabled={!sextforceActive}
                                        onChange={e => {
                                            const newActiveStatus = e.target.checked;
                                            if (!sextforceActive || newActiveStatus === serviceActive) return;
                                            updateAutoKickSettingsService(newActiveStatus).then(success => {
                                                if (success) setServiceActive(newActiveStatus);
                                            });
                                        }}
                                        color="primary"
                                    />
                                )}
                            </Grid>
                        </Grid>

                        {!sextforceActive && (
                            <Alert severity="error" sx={{ mt: 2 }}>
                                Sextforce service must be active to use this feature
                            </Alert>
                        )}
                    </StyledCard>
                    <StyledCard>
                        <LoadingButton
                            loading={kickAllLoading}
                            variant="contained"
                            color="secondary"
                            fullWidth
                            size="large"
                            onClick={() => {
                                dialog
                                    .confirm({
                                        title: 'Boot All Non-Spenders?',
                                        message: 'Are you sure you want to boot all current non-spenders from your live stream?',
                                        cancel: {
                                            text: 'Cancel',
                                            color: 'primary',
                                            variant: 'outlined',
                                        },
                                        ok: {
                                            text: 'Boot Them All',
                                            color: 'error',
                                            variant: 'contained',
                                        },
                                    })
                                    .then(() => {
                                        kickAllButton();
                                    })
                                    .catch(() => {
                                        // do nothing on cancel
                                    });
                            }}
                        >
                            <span>⚡ BOOT ALL NON-SPENDERS RIGHT NOW</span>
                        </LoadingButton>
                    </StyledCard>

                    {/* GOALS FEATURE — Auto Kick mode selector commented out (base auto-kick only for now;
                        restore from commits ce54e3b4 / 26d5d814):

                    <StyledCard>
                        <Typography variant="subtitle2" gutterBottom>
                            Auto Kick settings
                        </Typography>
                        <ToggleButtonGroup
                            exclusive
                            fullWidth
                            color="primary"
                            size="small"
                            value={perGoalMode ? 'perGoal' : 'wholeStream'}
                            disabled={!sextforceActive || modeLoading}
                            onChange={(_, val) => {
                                if (val === 'wholeStream') changeKickMode(false);
                                else if (val === 'perGoal') changeKickMode(true);
                            }}
                        >
                            <ToggleButton value="wholeStream">Same settings all stream</ToggleButton>
                            <ToggleButton value="perGoal">Settings change per goal</ToggleButton>
                        </ToggleButtonGroup>
                        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                            {perGoalMode
                                ? "Auto-kick follows your Tip Goals below - each goal uses its own settings while it is the active goal."
                                : 'One set of auto-kick settings applies for the entire stream.'}
                        </Typography>
                    </StyledCard>
                    */}

                    <StyledCard noCard noBottomMargin>
                        <StyledCardTitleBar title="Settings" />
                    </StyledCard>
                    <StyledCard>
                        <SliderSetting
                            label="Tips From How Long Ago Count"
                            value={tippedHoursAgo}
                            onChange={setTippedHoursAgo}
                            min={0}
                            max={72}
                            unit="hours"
                            helperText="Only tips within this window count as 'paid up'. Set to 0 to require a tip during the current stream."
                        />

                        <SliderSetting
                            label="Minimum Tip Amount"
                            value={minimumTipAmount}
                            onChange={setMinimumTipAmount}
                            min={1}
                            max={200}
                            unit="Dollars"
                            helperText="Set the minimum tip amount that counts as 'paid'. If they tip below this amount, they'll still get kicked as a freeloader. Set to 1 for no minimum."
                        />

                        <SliderSetting
                            label="Total Time Users Can Watch Free"
                            value={usersCanWatchMinutes}
                            onChange={setUsersCanWatchMinutes}
                            min={4}
                            max={120}
                            unit="minutes"
                            helperText="How long a freeloader gets to enjoy the show before warnings kick in. 4 minutes minimum."
                        />

                        <SliderSetting
                            label="Number Of Rejoins After Being Booted Allowed"
                            value={numberOfRejoinsAllowed}
                            onChange={setNumberOfRejoinsAllowed}
                            min={0}
                            max={10}
                            unit="times"
                            helperText="Give kicked fans a chance to come back and open their wallet — or set to 0 for a one-strike policy."
                        />

                        <SliderSetting
                            label="Total Time Users Can Watch Free After Rejoining"
                            value={usersCanWatchMinutesOnRejoin}
                            onChange={setUsersCanWatchMinutesOnRejoin}
                            min={4}
                            max={120}
                            unit="minutes"
                            disabled={numberOfRejoinsAllowed === 0}
                            helperText="How long they get on their second (or third...) chance before they're out again."
                        />

                        <Grid mb={4} pl={1}>
                            <FormControlLabel
                                control={<Switch checked={sendWarningMessages} onChange={e => setSendWarningMessages(e.target.checked)} />}
                                label="Warn fans before kicking (recommended)"
                            />
                        </Grid>
                        <Grid mb={4} pl={1}>
                            <FormControlLabel
                                disabled={!sendWarningMessages}
                                control={
                                    <Switch checked={onlySendFinalWarning} onChange={e => setOnlySendFinalWarning(e.target.checked)} />
                                }
                                label="Only send the last warning instead of all 3."
                            />
                        </Grid>
                        <Grid mb={4} pl={1}>
                            <Typography variant="subtitle2" gutterBottom>
                                Select an OnlyFans List you would like to use as a whitelist. Anyone on this list will not get kicked by the
                                auto kick feature.
                            </Typography>
                            <OnlyFansListsSelector
                                includeBuiltInLists={false}
                                targetList={whiteListListId ? { id: parseInt(whiteListListId, 10), name: '' } : undefined}
                                setTargetList={targetList => {
                                    setWhiteListListId(targetList?.id.toString() ?? '0');
                                }}
                                includeSelectNoneOption={true}
                                showCreateListButton={true}
                                size="small"
                            />
                            {/* <ListSingleSelect
                                options={lists.filter(item => item.canAddUsers && item.type === 'custom')}
                                loading={hasMore}
                                value={lists.find(s => s.id.toString() === whiteListListId) ?? null}
                                onChange={next => {
                                    setWhiteListListId(next?.id.toString() ?? '0');
                                }}
                                title="White List this Onlyfans List (optional)"
                            /> */}
                        </Grid>
                        <Grid>
                            <LoadingButton
                                loading={updateSettingsLoading}
                                variant="contained"
                                // Switches to secondary (highlighted) only when there are unsaved changes
                                color={hasChanges ? 'secondary' : 'primary'}
                                fullWidth
                                size="large"
                                onClick={updateAutoKick}
                                sx={{ transition: 'background-color 0.3s' }}
                            >
                                <span>{hasChanges ? 'Save Changes' : 'Saved'}</span>
                            </LoadingButton>
                        </Grid>
                    </StyledCard>

                    {/* GOALS FEATURE — Tip Goals editor commented out (base auto-kick only for now;
                        restore from commits ce54e3b4 / 26d5d814):

                    <StyledCard noCard noBottomMargin>
                        <StyledCardTitleBar title="Tip Goals" />
                    </StyledCard>
                    <LiveStreamGoalsEditor
                        subscriber={subscriber}
                        sextforceActive={sextforceActive}
                        showKickSettings={serviceActive && perGoalMode}
                    />
                    */}

                    <StyledCard noCard noBottomMargin>
                        <StyledCardTitleBar title="Whitelist Fans" />
                    </StyledCard>
                    <StyledCard>
                        <Grid>
                            <Typography variant="subtitle2" gutterBottom>
                                Use the below box to search for individual fans you want to add to a whitelist so they don't get kicked out!
                                Great if you have moderators on your live stream.
                            </Typography>
                            <Autocomplete<kickTimeWastersWhiteList, false, false, false>
                                options={
                                    searchResults?.map(item => {
                                        return {
                                            onlyfansId: subscriber.ofUserId,
                                            fanId: item.id,
                                            fanName: item.username,
                                            fanDisplayName: item.name,
                                        };
                                    }) ?? []
                                }
                                loading={whiteListLoading || addWhiteListLoading}
                                disabled={addWhiteListLoading}
                                value={null}
                                inputValue={queryStrDebounce}
                                getOptionLabel={opt => `(${opt.fanDisplayName}) ${opt.fanName}`}
                                filterOptions={x => x}
                                isOptionEqualToValue={(a, b) => a.fanId === b.fanId}
                                onInputChange={(_, newValue) => searchOnChange(newValue)}
                                onChange={(_, newValue) => addToWhiteList(newValue)}
                                getOptionDisabled={option => whitelistIds.has(option.fanId)}
                                renderOption={(props, option) => {
                                    const alreadyAdded = whitelistIds.has(option.fanId);
                                    return (
                                        <li {...props} key={option.fanId} aria-disabled={alreadyAdded}>
                                            <Box
                                                sx={{
                                                    width: '100%',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'space-between',
                                                }}
                                            >
                                                <Typography variant="body2">{`(${option.fanDisplayName}) ${option.fanName}`}</Typography>
                                                {alreadyAdded && (
                                                    <Typography variant="caption" sx={{ opacity: 0.7 }}>
                                                        Added
                                                    </Typography>
                                                )}
                                            </Box>
                                        </li>
                                    );
                                }}
                                renderInput={params => (
                                    <TextField
                                        {...params}
                                        label={`Search`}
                                        placeholder={`Type to search...`}
                                        InputProps={{
                                            ...params.InputProps,
                                            endAdornment: (
                                                <>
                                                    {whiteListLoading || addWhiteListLoading ? (
                                                        <CircularProgress color="inherit" size={18} />
                                                    ) : null}
                                                    {params.InputProps.endAdornment}
                                                </>
                                            ),
                                        }}
                                    />
                                )}
                            />
                            <Paper elevation={0} sx={{ p: 0, mt: 1 }}>
                                {whiteList.length === 0 ? (
                                    <Typography variant="body2" color="text.secondary">
                                        No fans in whitelist
                                    </Typography>
                                ) : (
                                    <>
                                        <List sx={{ maxHeight: 300, overflow: 'auto' }}>
                                            {whiteList.map((fan, index) => (
                                                <Fragment key={fan.fanId}>
                                                    <ListItem
                                                        disableGutters
                                                        secondaryAction={
                                                            <IconButton
                                                                aria-label="remove"
                                                                disabled={removingFanId !== null}
                                                                onClick={() => {
                                                                    dialog
                                                                        .confirm({
                                                                            title: 'Remove from Whitelist?',
                                                                            message: `Are you sure you want to remove ${fan.fanDisplayName} (@${fan.fanName}) from the whitelist? They will be kicked out on the next auto kick cycle if they haven't tipped.`,
                                                                            cancel: {
                                                                                text: 'Cancel',
                                                                                color: 'primary',
                                                                                variant: 'outlined',
                                                                            },
                                                                            ok: {
                                                                                text: 'Remove',
                                                                                color: 'error',
                                                                                variant: 'contained',
                                                                            },
                                                                        })
                                                                        .then(() => {
                                                                            removeFromWhiteList(fan);
                                                                        })
                                                                        .catch(() => {
                                                                            // do nothing on cancel
                                                                        });
                                                                }}
                                                                color="error"
                                                            >
                                                                {removingFanId === fan.fanId ? (
                                                                    <CircularProgress color="inherit" size={22} />
                                                                ) : (
                                                                    <CloseIcon />
                                                                )}
                                                            </IconButton>
                                                        }
                                                    >
                                                        <ListItemText
                                                            primary={
                                                                <Typography variant="inherit">
                                                                    {fan.fanDisplayName}{' '}
                                                                    <Link
                                                                        to={`https://onlyfans.com/${fan.fanName}`}
                                                                        target="_blank"
                                                                        rel="noopener noreferrer"
                                                                        style={{
                                                                            textDecoration: 'none',
                                                                        }}
                                                                    >
                                                                        <Stack
                                                                            direction="row"
                                                                            alignItems="center"
                                                                            spacing={0.5}
                                                                            component="span"
                                                                        >
                                                                            <Typography variant="caption" color="text.secondary">
                                                                                @{fan.fanName}
                                                                            </Typography>
                                                                            <OpenInNewIcon sx={{ fontSize: 'inherit' }} />
                                                                        </Stack>
                                                                    </Link>
                                                                </Typography>
                                                            }
                                                        />
                                                    </ListItem>
                                                    {index < whiteList.length - 1 && <Divider />}
                                                </Fragment>
                                            ))}
                                        </List>
                                    </>
                                )}
                            </Paper>
                        </Grid>
                    </StyledCard>
                    <StyledCard noCard noBottomMargin>
                        <StyledCardTitleBar title="Blacklist Fans" />
                    </StyledCard>
                    <StyledCard>
                        <Grid>
                            <Typography variant="subtitle2" gutterBottom>
                                Use the below box to search for individual fans you want to blacklist so they always get kicked out
                                immediately, regardless of tips or whitelist settings.
                            </Typography>
                            <Autocomplete<kickTimeWastersBlackList, false, false, false>
                                options={
                                    blackSearchResults && subscriber.ofUserId
                                        ? [
                                              {
                                                  onlyfansId: subscriber.ofUserId,
                                                  fanId: blackSearchResults.id,
                                                  fanName: blackSearchResults.username,
                                                  fanDisplayName: blackSearchResults.name,
                                              },
                                          ]
                                        : []
                                }
                                loading={blackListLoading || addBlackListLoading}
                                disabled={addBlackListLoading}
                                value={null}
                                inputValue={blackQueryStrDebounce}
                                getOptionLabel={opt => `(${opt.fanDisplayName}) ${opt.fanName}`}
                                filterOptions={x => x}
                                isOptionEqualToValue={(a, b) => a.fanId === b.fanId}
                                onInputChange={(_, newValue) => blackSearchOnChange(newValue)}
                                onChange={(_, newValue) => addToBlackList(newValue)}
                                getOptionDisabled={option => blacklistIds.has(option.fanId)}
                                renderOption={(props, option) => {
                                    const alreadyAdded = blacklistIds.has(option.fanId);
                                    return (
                                        <li {...props} key={option.fanId} aria-disabled={alreadyAdded}>
                                            <Box
                                                sx={{
                                                    width: '100%',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'space-between',
                                                }}
                                            >
                                                <Typography variant="body2">{`(${option.fanDisplayName}) ${option.fanName}`}</Typography>
                                                {alreadyAdded && (
                                                    <Typography variant="caption" sx={{ opacity: 0.7 }}>
                                                        Added
                                                    </Typography>
                                                )}
                                            </Box>
                                        </li>
                                    );
                                }}
                                renderInput={params => (
                                    <TextField
                                        {...params}
                                        label={`Search`}
                                        placeholder={`Type to search...`}
                                        InputProps={{
                                            ...params.InputProps,
                                            endAdornment: (
                                                <>
                                                    {blackListLoading || addBlackListLoading ? (
                                                        <CircularProgress color="inherit" size={18} />
                                                    ) : null}
                                                    {params.InputProps.endAdornment}
                                                </>
                                            ),
                                        }}
                                    />
                                )}
                            />
                            <Paper elevation={0} sx={{ p: 0, mt: 1 }}>
                                {blackList.length === 0 ? (
                                    <Typography variant="body2" color="text.secondary">
                                        No fans in blacklist
                                    </Typography>
                                ) : (
                                    <>
                                        <List sx={{ maxHeight: 300, overflow: 'auto' }}>
                                            {blackList.map((fan, index) => (
                                                <Fragment key={fan.fanId}>
                                                    <ListItem
                                                        disableGutters
                                                        secondaryAction={
                                                            <IconButton
                                                                aria-label="remove"
                                                                disabled={removingBlackFanId !== null}
                                                                onClick={() => {
                                                                    dialog
                                                                        .confirm({
                                                                            title: 'Remove from Blacklist?',
                                                                            message: `Are you sure you want to remove ${fan.fanDisplayName} (@${fan.fanName}) from the blacklist?`,
                                                                            cancel: {
                                                                                text: 'Cancel',
                                                                                color: 'primary',
                                                                                variant: 'outlined',
                                                                            },
                                                                            ok: {
                                                                                text: 'Remove',
                                                                                color: 'error',
                                                                                variant: 'contained',
                                                                            },
                                                                        })
                                                                        .then(() => {
                                                                            removeFromBlackList(fan);
                                                                        })
                                                                        .catch(() => {
                                                                            // do nothing on cancel
                                                                        });
                                                                }}
                                                                color="error"
                                                            >
                                                                {removingBlackFanId === fan.fanId ? (
                                                                    <CircularProgress color="inherit" size={22} />
                                                                ) : (
                                                                    <CloseIcon />
                                                                )}
                                                            </IconButton>
                                                        }
                                                    >
                                                        <ListItemText
                                                            primary={
                                                                <Typography variant="inherit">
                                                                    {fan.fanDisplayName}{' '}
                                                                    <Link
                                                                        to={`https://onlyfans.com/${fan.fanName}`}
                                                                        target="_blank"
                                                                        rel="noopener noreferrer"
                                                                        style={{
                                                                            textDecoration: 'none',
                                                                        }}
                                                                    >
                                                                        <Stack
                                                                            direction="row"
                                                                            alignItems="center"
                                                                            spacing={0.5}
                                                                            component="span"
                                                                        >
                                                                            <Typography variant="caption" color="text.secondary">
                                                                                @{fan.fanName}
                                                                            </Typography>
                                                                            <OpenInNewIcon sx={{ fontSize: 'inherit' }} />
                                                                        </Stack>
                                                                    </Link>
                                                                </Typography>
                                                            }
                                                        />
                                                    </ListItem>
                                                    {index < blackList.length - 1 && <Divider />}
                                                </Fragment>
                                            ))}
                                        </List>
                                    </>
                                )}
                            </Paper>
                        </Grid>
                    </StyledCard>
                </>
            )}
        </PageContainer>
    );
};

export default SextforceLiveStreamAutoKickFreeLoaders;
